import { useState, useEffect, useCallback } from "react";
import { METALS, CURRENCIES } from "./constants";
import { CurrencySwiper, CurrencySidebar } from "./components/CurrencyPanel";
import MarketDashboard from "./components/MarketDashboard";

const GOLDAPI_KEY = process.env.REACT_APP_GOLDAPI_KEY;

const SYMBOL_MAP = { gold: "XAU", silver: "XAG", platinum: "XPT", palladium: "XPD" };

async function fetchMetalFromAPI(metalId) {
  const symbol = SYMBOL_MAP[metalId];
  const isDev = process.env.NODE_ENV === "development";
  const url = isDev ? `/api/${symbol}/USD` : `https://www.goldapi.io/api/${symbol}/USD`;
  const headers = isDev ? {} : { "x-access-token": GOLDAPI_KEY, "Content-Type": "application/json" };
  if (!isDev && !GOLDAPI_KEY) throw new Error("Missing REACT_APP_GOLDAPI_KEY in .env");
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || err.error || `HTTP ${res.status}`);
  }
  const d = await res.json();
  if (d.price == null) throw new Error(d.message || d.error || "Invalid response");
  return {
    price: d.price,
    prevClose: d.prev_close_price ?? d.prevClosePrice ?? d.price,
    prevOpen: d.open_price ?? d.openPrice ?? d.price,
    high: d.high_price ?? d.highPrice ?? d.price,
    low: d.low_price ?? d.lowPrice ?? d.price,
    change: d.ch ?? 0,
    changePct: d.chp ?? 0,
    currency: "USD",
    unit: "troy oz",
    timestamp: new Date().toISOString(),
  };
}

async function fetchCurrenciesFromAPI() {
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];
  const [resToday, resYest] = await Promise.all([
    fetch("https://api.frankfurter.app/latest?from=USD"),
    fetch(`https://api.frankfurter.app/${yesterday}?from=USD`),
  ]);
  if (!resToday.ok || !resYest.ok) throw new Error("FX fetch failed");
  const [dToday, dYest] = await Promise.all([resToday.json(), resYest.json()]);
  const result = {};
  CURRENCIES.forEach((c) => {
    const rate = parseFloat(dToday.rates[c.code]) || 0;
    const prevClose = parseFloat(dYest.rates[c.code]) || rate;
    const change = parseFloat((rate - prevClose).toFixed(4));
    const changePct = parseFloat(((change / prevClose) * 100).toFixed(3));
    result[c.id] = { rate, prevClose, change, changePct,
      high: Math.max(rate, prevClose), low: Math.min(rate, prevClose),
      timestamp: new Date().toISOString() };
  });
  return result;
}

const fmt     = (n) => n?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTime = (iso) => { if (!iso) return "—"; return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); };
const fmtDate = (iso) => { if (!iso) return "—"; return new Date(iso).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }); };

function DetailRow({ label, value, highlight, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid #1e1e2a" }}>
      <span style={{ color: "#666", fontSize: 14 }}>{label}</span>
      <span style={{ color: color || (highlight ? "#fff" : "#bbb"), fontWeight: highlight ? 700 : 500, fontSize: highlight ? 18 : 15, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}

function DetailScreen({ metal, data, onBack }) {
  const isUp = data.change >= 0;
  return (
    <div className="screen" style={{ minHeight: "100vh", background: "#08080f", color: "#fff", fontFamily: "'DM Sans',sans-serif" }}>
      <div className="screen-header" style={{ background: `linear-gradient(180deg,${metal.bg} 0%,#08080f 100%)`, padding: "24px 20px 40px", position: "relative", overflow: "hidden" }}>
        <div className="screen-glow" style={{ background: `${metal.accent}15` }} />
        <button className="back-btn" onClick={onBack}>← Back</button>
        <div className="screen-title-row">
          <span style={{ fontSize: 32, color: metal.accent }}>{metal.icon}</span>
          <div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>{metal.name}</h1>
            <div style={{ color: "#555", fontSize: 13, letterSpacing: 1 }}>{metal.symbol} · {metal.karat} · per troy oz</div>
          </div>
        </div>
        <div className="screen-price" style={{ color: metal.color }}>${fmt(data.price)}</div>
        <div className="screen-badge" style={{ color: isUp ? "#4ade80" : "#f87171", background: isUp ? "#4ade8020" : "#f8717120" }}>
          {isUp ? "▲ +" : "▼ "}{fmt(data.change)} ({isUp ? "+" : ""}{data.changePct}%)
        </div>
      </div>
      <div className="detail-body" style={{ padding: "0 20px 40px" }}>
        <div className="detail-card">
          <DetailRow label="Current Price"  value={`$${fmt(data.price)}`}    highlight color={metal.color} />
          <DetailRow label="Previous Close" value={`$${fmt(data.prevClose)}`} />
          <DetailRow label="Previous Open"  value={`$${fmt(data.prevOpen)}`} />
          <DetailRow label="Today's High"   value={`$${fmt(data.high)}`}     color="#4ade80" />
          <DetailRow label="Today's Low"    value={`$${fmt(data.low)}`}      color="#f87171" />
          <DetailRow label="Change"         value={`${isUp?"+":""}${fmt(data.change)} USD`} color={isUp?"#4ade80":"#f87171"} />
          <DetailRow label="Change %"       value={`${isUp?"+":""}${data.changePct}%`}      color={isUp?"#4ade80":"#f87171"} />
          <DetailRow label="Currency"       value={data.currency} />
          <DetailRow label="Unit"           value={data.unit} />
          <DetailRow label="Today's Date"   value={fmtDate(data.timestamp)} />
          <DetailRow label="Last Updated"   value={fmtTime(data.timestamp)} />
        </div>
        <div className="range-card">
          <div className="range-label">DAY RANGE</div>
          <div className="range-bar">
            <div className="range-fill" style={{ width: `${((data.price - data.low) / (data.high - data.low)) * 100}%`, background: `linear-gradient(90deg,${metal.accent}66,${metal.accent})` }} />
            <div className="range-dot" style={{ left: `${((data.price - data.low) / (data.high - data.low)) * 100}%`, background: metal.accent }} />
          </div>
          <div className="range-ends">
            <span className="down">L ${fmt(data.low)}</span>
            <span className="up">H ${fmt(data.high)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CurrencyDetailScreen({ currency, data, onBack }) {
  const isUp = data.change >= 0;
  const dp = (currency.id === "jpy" || currency.id === "inr") ? 2 : 4;
  return (
    <div className="screen" style={{ minHeight: "100vh", background: "#08080f", color: "#fff", fontFamily: "'DM Sans',sans-serif" }}>
      <div className="screen-header" style={{ background: "linear-gradient(180deg,#0d0d18 0%,#08080f 100%)", padding: "24px 20px 40px", position: "relative", overflow: "hidden" }}>
        <div className="screen-glow" style={{ background: `${currency.accent}12` }} />
        <button className="back-btn" onClick={onBack}>← Back</button>
        <div className="screen-title-row">
          <span style={{ fontSize: 36 }}>{currency.flag}</span>
          <div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>{currency.name}</h1>
            <div style={{ color: "#555", fontSize: 13, letterSpacing: 1 }}>USD / {currency.code}</div>
          </div>
        </div>
        <div className="screen-price" style={{ color: currency.color }}>{data.rate.toFixed(dp)}</div>
        <div className="screen-badge" style={{ color: isUp ? "#4ade80" : "#f87171", background: isUp ? "#4ade8020" : "#f8717120" }}>
          {isUp ? "▲ +" : "▼ "}{data.change.toFixed(dp)} ({isUp?"+":""}{data.changePct}%)
        </div>
      </div>
      <div className="detail-body" style={{ padding: "0 20px 40px" }}>
        <div className="detail-card">
          <DetailRow label="Current Rate"   value={data.rate.toFixed(dp)}      highlight color={currency.color} />
          <DetailRow label="Previous Close" value={data.prevClose.toFixed(dp)} />
          <DetailRow label="Today's High"   value={data.high.toFixed(dp)}      color="#4ade80" />
          <DetailRow label="Today's Low"    value={data.low.toFixed(dp)}       color="#f87171" />
          <DetailRow label="Change"         value={`${isUp?"+":""}${data.change.toFixed(dp)}`} color={isUp?"#4ade80":"#f87171"} />
          <DetailRow label="Change %"       value={`${isUp?"+":""}${data.changePct}%`}         color={isUp?"#4ade80":"#f87171"} />
          <DetailRow label="Base Currency"  value="USD" />
          <DetailRow label="Today's Date"   value={fmtDate(data.timestamp)} />
          <DetailRow label="Last Updated"   value={fmtTime(data.timestamp)} />
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState("home");
  const [selectedMetal, setSelectedMetal] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedFxData, setSelectedFxData] = useState(null);

  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [fxPrices, setFxPrices] = useState({});
  const [fxLoading, setFxLoading] = useState({});
  const [fxErrors, setFxErrors] = useState({});

  const fetchMetal = useCallback(async (id) => {
    setLoading((p) => ({ ...p, [id]: true }));
    setErrors((p) => ({ ...p, [id]: null }));
    try {
      const d = await fetchMetalFromAPI(id);
      setPrices((p) => ({ ...p, [id]: d }));
    } catch (e) {
      setErrors((p) => ({ ...p, [id]: e.message }));
    } finally {
      setLoading((p) => ({ ...p, [id]: false }));
    }
  }, []);

  const fetchCurrencies = useCallback(async () => {
    const loadingState = Object.fromEntries(CURRENCIES.map((c) => [c.id, true]));
    setFxLoading(loadingState);
    setFxErrors({});
    try {
      const rates = await fetchCurrenciesFromAPI();
      setFxPrices(rates);
    } catch (e) {
      setFxErrors(
        Object.fromEntries(CURRENCIES.map((c) => [c.id, e.message]))
      );
    } finally {
      setFxLoading(
        Object.fromEntries(CURRENCIES.map((c) => [c.id, false]))
      );
    }
  }, []);

  const refresh = useCallback(() => {
    METALS.forEach((m) => fetchMetal(m.id));
    fetchCurrencies();
  }, [fetchMetal, fetchCurrencies]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (screen === "detail" && selectedMetal && selectedData)
    return <DetailScreen metal={selectedMetal} data={selectedData} onBack={() => setScreen("home")} />;
  if (screen === "fxdetail" && selectedCurrency && selectedFxData)
    return <CurrencyDetailScreen currency={selectedCurrency} data={selectedFxData} onBack={() => setScreen("home")} />;

  const handleCurrencyPress = (cur, data) => {
    setSelectedCurrency(cur);
    setSelectedFxData(data);
    setScreen("fxdetail");
  };

  const handleMetalPress = (m, d) => {
    setSelectedMetal(m);
    setSelectedData(d);
    setScreen("detail");
  };

  return (
    <div className="layout">
      <CurrencySidebar
        fxPrices={fxPrices}
        fxLoading={fxLoading}
        fxErrors={fxErrors}
        onPress={handleCurrencyPress}
      />
      <MarketDashboard
        prices={prices}
        loading={loading}
        errors={errors}
        onMetalPress={handleMetalPress}
        onRefresh={refresh}
      >
        <CurrencySwiper
          fxPrices={fxPrices}
          fxLoading={fxLoading}
          fxErrors={fxErrors}
          onPress={handleCurrencyPress}
        />
      </MarketDashboard>
    </div>
  );
}
